type Rgb = [number, number, number];
type Hsl = [number, number, number];

/** Parse #rgb / #rrggbb / hsl() into 0–255 channels; null for anything else. */
export function parseColor(input: string): Rgb | null {
    const s = input.trim();
    const hsl = s.match(/^hsla?\(\s*([\d.]+)(?:deg)?[,\s]+([\d.]+)%[,\s]+([\d.]+)%/i);
    if (hsl) return hslToRgb([Number(hsl[1]) / 360, Number(hsl[2]) / 100, Number(hsl[3]) / 100]);
    const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
        let h = hex[1];
        if (h.length === 3)
            h = h
                .split('')
                .map((c) => c + c)
                .join('');
        const n = parseInt(h, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    return null;
}

function rgbToHsl([r, g, b]: Rgb): Hsl {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h: number;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h / 6, s, l];
}

function hslToRgb([h, s, l]: Hsl): Rgb {
    if (s === 0) return [l * 255, l * 255, l * 255];
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const f = (t: number) => {
        t = ((t % 1) + 1) % 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255];
}

function hslToCss([h, s, l]: Hsl): string {
    return `hsl(${(h * 360).toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%)`;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * A shade of a colour as hsl(): `dl` moves the lightness (−1..1), `ds` the
 * saturation. Darker shades get a touch more saturation so they stay rich
 * instead of going grey, the way a painted surface falls into shadow.
 */
export function shade(color: string, dl: number, ds = 0): string {
    const c = parseColor(color);
    if (!c) return color;
    const [h, s, l] = rgbToHsl(c);
    return hslToCss([h, clamp01(s + ds + (dl < 0 ? -dl * 0.25 : 0)), clamp01(l + dl)]);
}
